import { memo, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';

import { Dropdown } from 'basic-components';
import { DataLakeEnums, DataLakeReadableConsts } from 'dashboard-services/api/dataLake';

import groupConfigService from '../../groupConfigService';
import useGroupConfigHandlersModule from '../../hooks/useGroupConfigHandlers';
import NcsvColumns from '../NcsvColumns';
import ToggleRow from '../ToggleRow';
import DynamicValidation from './DynamicValidation';

import { When } from 'react-if';

import './ValidationSection.scss';

const EMPTY_ARRAY = []
const EMPTY_OBJ = {}
const { useGroupConfigHandlers } = useGroupConfigHandlersModule

const ValidationSection = ({ dynamicValidation, errors, groupConfig, menuPortalTarget, onChangeValidationOptions, setDynamicValidation, setGroupConfig, validationOptions, validatorTask }) => {
  const { symbolsConfig = EMPTY_OBJ, filesConfigs = EMPTY_ARRAY, isDynamicValidationEnabled = false } = groupConfig,
        { isRestrictFileTypesOn, shouldRemoveInvalidParams } = validationOptions,
        { onChangeGroupConfig, onChangeSymbolsConfigSwitch, onChangeFileConfig, onChangeFileTypes } = useGroupConfigHandlers({ setGroupConfig }),
        selectedFileTypes = useMemo(() => groupConfigService.getSelectedFileTypes(filesConfigs), [filesConfigs]),
        defaultFileTypes = useMemo(() => selectedFileTypes.map(fileType => DataLakeReadableConsts.FILE_TYPES.find(option => option.value === fileType) || { value: fileType, label: fileType }), [selectedFileTypes]),
        ncsvIndex = useMemo(() => groupConfigService.getFirstFileConfigIndex({ filesConfigs, fileType: DataLakeEnums.FILE_TYPE.NCSV }), [filesConfigs]),
        onChangeFileTypesLocal = useCallback(fileTypes => onChangeFileTypes(fileTypes || []), [onChangeFileTypes])

  return (
    <div className="data-lake__group-dialog__advanced__validation-section">
      <ToggleRow
          label="Create new symbols"
          name="doNotAllowCreatingNewSymbols"
          on={!symbolsConfig.doNotAllowCreatingNewSymbols}
          onChange={onChangeSymbolsConfigSwitch}
      />
      <ToggleRow
          label="Create new columns"
          name="doNotAllowCreatingNewColumns"
          on={!symbolsConfig.doNotAllowCreatingNewColumns}
          onChange={onChangeSymbolsConfigSwitch}
      />
      <ToggleRow
          label="Restrict uploads to selected file types"
          name="isRestrictFileTypesOn"
          on={isRestrictFileTypesOn}
          onChange={onChangeValidationOptions}
      />
      <When condition={isRestrictFileTypesOn}>
        {() => (
          <div className="data-lake__group-dialog__advanced__validation-section__file-types">
            <Dropdown
                defaultValue={defaultFileTypes}
                menuPortalTarget={menuPortalTarget}
                multi
                name="fileTypes"
                noError
                onChange={onChangeFileTypesLocal}
                options={DataLakeReadableConsts.FILE_TYPES}
                placeholder="Select file types"
                variant="border"
            />
            <When condition={ncsvIndex > -1}>
              {() => (
                <NcsvColumns
                    index={ncsvIndex}
                    mandatoryColumns={filesConfigs[ncsvIndex].mandatoryColumns}
                    onChange={onChangeFileConfig}
                    optionalColumns={filesConfigs[ncsvIndex].optionalColumns}
                />
              )}
            </When>
          </div>
        )}
      </When>
      <ToggleRow
          label="Use dynamic validation"
          name="isDynamicValidationEnabled"
          on={isDynamicValidationEnabled}
          onChange={onChangeGroupConfig}
      />
      <When condition={isDynamicValidationEnabled}>
        {() => (
          <DynamicValidation
              continueIfTaskNotFound={dynamicValidation?.continueIfTaskNotFound}
              errors={errors}
              menuPortalTarget={menuPortalTarget}
              onChangeValidationOptions={onChangeValidationOptions}
              setDynamicValidation={setDynamicValidation}
              shouldRemoveInvalidParams={shouldRemoveInvalidParams}
              validatorTask={validatorTask}
          />
        )}
      </When>
    </div>
  )
}

ValidationSection.propTypes = {
  dynamicValidation: PropTypes.object,
  errors: PropTypes.array.isRequired,
  groupConfig: PropTypes.object.isRequired,
  menuPortalTarget: PropTypes.object,
  onChangeValidationOptions: PropTypes.func.isRequired,
  setDynamicValidation: PropTypes.func.isRequired,
  setGroupConfig: PropTypes.func.isRequired,
  validationOptions: PropTypes.shape({ isRestrictFileTypesOn: PropTypes.bool, shouldRemoveInvalidParams: PropTypes.bool }).isRequired,
  validatorTask: PropTypes.object.isRequired
}

export default memo(ValidationSection)
